import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, AlertTriangle, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import EvidenceUploadModal from "../components/EvidenceUploadModal";
import { toast } from "sonner";

const STATUS_LABEL = { valid: "Válida", expiring_soon: "Próxima a caducar", expired: "Caducada", archived: "Archivada" };
const STATUS_CLS = {
  valid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expiring_soon: "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  archived: "bg-slate-100 text-slate-500",
};

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; } }

export default function Evidences() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUpload = ["owner", "admin", "compliance_manager"].includes(user?.role);

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "all", q: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ page: String(page), page_size: "25" });
        if (filters.status !== "all") qs.set("status", filters.status);
        if (filters.q) qs.set("q", filters.q);
        const [{ data: list }, { data: a }] = await Promise.all([
          api.get(`/evidences?${qs}`), api.get("/evidence-alerts"),
        ]);
        if (!cancelled) { setData(list); setAlerts(a); }
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, filters]);

  const totalPages = Math.max(1, Math.ceil(data.total / 25));
  const expiringCount = alerts.filter((a) => a.alert_type === "expiring_soon").length;
  const expiredCount = alerts.filter((a) => a.alert_type === "expired").length;

  return (
    <div data-testid="evidences-page" className="max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Evidencias</h1>
          <p className="text-[14px] text-slate-500 mt-1">Pruebas de cumplimiento asociadas a controles.</p>
        </div>
        {canUpload && (
          <Button onClick={() => setUploadOpen(true)} data-testid="btn-upload-evidence">
            <Upload className="h-4 w-4 mr-1" /> Subir evidencia
          </Button>
        )}
      </div>

      {(expiringCount > 0 || expiredCount > 0) && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 flex items-center gap-3" data-testid="alerts-banner">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <div className="text-[14px] text-amber-900">
            Tienes <button onClick={() => { setFilters({ ...filters, status: "expiring_soon" }); setPage(1); }} className="underline font-medium">{expiringCount} evidencias próximas a caducar</button>
            {" y "}
            <button onClick={() => { setFilters({ ...filters, status: "expired" }); setPage(1); }} className="underline font-medium">{expiredCount} caducadas</button>.
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-3 flex-wrap">
        <Input className="w-72" placeholder="Buscar por título o descripción..." value={filters.q} onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }} data-testid="evidence-search" />
        <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Título</TableHead>
            <TableHead>Controles</TableHead>
            <TableHead>Recogida</TableHead>
            <TableHead>Caduca</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Tamaño</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Cargando...</TableCell></TableRow>}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">Sin evidencias.</TableCell></TableRow>
            )}
            {!loading && data.items.map((e) => {
              const ctrls = e.related_control_ids || [];
              return (
                <TableRow key={e.id} onClick={() => navigate(`/evidences/${e.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`evidence-row-${e.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-slate-400" />
                      <span className="font-medium text-slate-900">{e.title}</span>
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">{e.file_name}</div>
                  </TableCell>
                  <TableCell className="text-[12px] text-slate-600">{ctrls.length} control{ctrls.length === 1 ? "" : "es"}</TableCell>
                  <TableCell className="text-[12px] text-slate-500">{fmt(e.collected_at)}</TableCell>
                  <TableCell className="text-[12px] text-slate-500">{e.expires_at ? fmt(e.expires_at) : "No caduca"}</TableCell>
                  <TableCell><Badge className={STATUS_CLS[e.status]}>{STATUS_LABEL[e.status]}</Badge></TableCell>
                  <TableCell className="text-[12px] text-slate-500">{(e.file_size_bytes / 1024 / 1024).toFixed(2)} MB</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4 text-[12px] text-slate-500">
        <span>Total: {data.total} · Página {page} de {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ev-prev">Anterior</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="ev-next">Siguiente</Button>
        </div>
      </div>

      {uploadOpen && (
        <EvidenceUploadModal
          open={uploadOpen} onClose={() => setUploadOpen(false)}
          onUploaded={(ev) => { setUploadOpen(false); navigate(`/evidences/${ev.id}`); }}
        />
      )}
    </div>
  );
}
